import {
  FilterSchema,
  HoofdstukSchema,
  PHBSchema,
  ParagraafSchema,
  SPPBSchema,
  SubparagraafSchema,
  VSPBSchema,
  VoorschriftSchema,
} from '@/api';
import { useBesluit } from '@/api/queries/besluit';
import {
  useHoofdstuk,
  usePHB,
  useParagraaf,
  useSPPB,
  useSubparagraaf,
  useVSPB,
  useVoorschrift,
} from '@/api/queries/objects';
import { GRONDSLAGEN, Grondslag } from '@/constants/grondslagen';
import { createMap } from '@/utils/createMap';
import { DateUtils } from '@/utils/dateUtils';
import { fetchAllPages } from '@/utils/fetchAllPages';
import { fetchInFilter } from '@/utils/fetchInFilter';
import { createQuery } from '@/utils/tanstack-query/createQuery';

export type VigerendData = {
  data: Grondslag;
  hoofdstukken: VigerendHoofdstuk[];
}[];

export type VigerendHoofdstuk = {
  data: HoofdstukSchema;
  paragrafen: VigerendParagraaf[];
};

export type VigerendParagraaf = {
  data: ParagraafSchema;
  phb: PHBSchema;
  subparagrafen: VigerendSubparagraaf[];
};

export type VigerendSubparagraaf = {
  data: SubparagraafSchema;
  sppb: SPPBSchema;
  voorschriften: VigerendVoorschrift[];
};

export type VigerendVoorschrift = {
  data: VoorschriftSchema;
  vspb: VSPBSchema;
};

export const useVigerendData = createQuery({
  key: ['links', 'vigerend'],
  queryFn: fetchVigerendData,
});

export async function fetchVigerendData({
  date,
  bedrijfLineageId,
}: {
  date: Date;
  bedrijfLineageId: string;
}): Promise<VigerendData> {
  /**
   *
   * Fetch a besluitLineageId that is linked to the bedrijf
   *
   */
  const besluitResponse = await useBesluit.fetchQuery({
    page: 1,
    size: 1,
    filter: [
      {
        model: 'Bedrijf',
        field: 'Lineage',
        op: 'eq',
        value: bedrijfLineageId,
      },
    ],
  });

  const besluitLineageId = besluitResponse.objects?.[0]?.Lineage;

  if (!besluitLineageId) {
    throw new Error('No besluit lineage id found');
  }

  const voorschriftFilter: FilterSchema[] = [
    {
      model: 'Voorschrift',
      field: 'timetravel',
      op: 'eq',
      value: `${DateUtils.formatForBE(date)},${besluitLineageId}`,
    },
    {
      model: 'VSPB',
      field: 'Type',
      op: 'eq',
      value: 'Eigen',
    },
  ];

  /**
   *
   * Fetch all VSPB's that are inwerking at the given date
   *
   */
  const [vspbs, voorschriften] = await Promise.all([
    useVSPB.fetchQuery({
      page: 1,
      size: 1000,
      filter: voorschriftFilter,
      sort: [
        {
          model: 'VSPB',
          field: 'Rang',
          direction: 'asc',
        },
      ],
    }),
    fetchAllPages((page) => {
      return useVoorschrift.fetchQuery({
        page,
        size: 100,
        filter: voorschriftFilter,
      });
    }),
  ]);

  const vspbSubparagraafIds = [
    ...new Set(vspbs.objects?.map((vspb) => vspb.Subparagraaf) ?? []),
  ];

  /**
   *
   * Fetch all parent links and objects
   *
   */
  const [sppbs, subparagrafen] = await Promise.all([
    fetchInFilter(
      ({ page, size, value }) =>
        useSPPB.fetchQuery({
          page,
          size,
          filter: [
            {
              field: 'Subparagraaf',
              op: 'in',
              value,
            },
          ],
        }),
      vspbSubparagraafIds
    ),
    fetchInFilter(
      ({ page, size, value }) =>
        useSubparagraaf.fetchQuery({
          page,
          size,
          filter: [{ model: 'Subparagraaf', field: 'ID', op: 'in', value }],
        }),
      vspbSubparagraafIds
    ),
  ]);

  const sppbParagraafIds = [
    ...new Set(sppbs.map((sppb) => sppb.Paragraaf) ?? []),
  ];

  const [phbs, paragrafen] = await Promise.all([
    fetchInFilter(
      ({ page, size, value }) =>
        usePHB.fetchQuery({
          page,
          size,
          filter: [
            {
              field: 'Paragraaf',
              op: 'in',
              value,
            },
          ],
        }),
      sppbParagraafIds
    ),
    fetchInFilter(
      ({ page, size, value }) =>
        useParagraaf.fetchQuery({
          page,
          size,
          filter: [{ field: 'ID', op: 'in', value }],
        }),
      sppbParagraafIds
    ),
  ]);

  const phbHoofdstukIds = [...new Set(phbs.map((phb) => phb.Hoofdstuk) ?? [])];

  const hoofdstukken = await fetchInFilter(
    ({ page, size, value }) =>
      useHoofdstuk.fetchQuery({
        page,
        size,
        filter: [
          {
            field: 'ID',
            op: 'in',
            value,
          },
        ],
      }),
    phbHoofdstukIds
  );

  /**
   *
   * Maps for quick lookup
   *
   */
  const voorschriftIdMap = createMap(voorschriften, 'ID');
  const subparagraafIdMap = createMap(subparagrafen, 'ID');
  const paragraafIdMap = createMap(paragrafen, 'ID');
  const hoofdstukIdMap = createMap(hoofdstukken, 'ID');

  const subparagraafIdToSppb = createMap(sppbs, 'Subparagraaf');
  const paragraafIdToPhb = createMap(phbs, 'Paragraaf');

  /**
   * Create nested structure
   */
  const vigerendSubparagrafenMap = new Map<string, VigerendSubparagraaf>();
  const vigerendParagrafenMap = new Map<string, VigerendParagraaf>();
  const vigerendHoofdstukkenMap = new Map<string, VigerendHoofdstuk>();

  for (const vspb of vspbs.objects ?? []) {
    /**
     * Get all objects for the current VSPB
     */
    const voorschrift = voorschriftIdMap.get(vspb.Voorschrift);
    const subparagraaf = subparagraafIdMap.get(vspb.Subparagraaf);
    const sppb = subparagraafIdToSppb.get(vspb.Subparagraaf);

    if (!voorschrift || !subparagraaf || !sppb) continue;

    const phb = paragraafIdToPhb.get(sppb.Paragraaf);
    const paragraaf = paragraafIdMap.get(sppb.Paragraaf);

    if (!phb || !paragraaf) continue;

    const hoofdstuk = hoofdstukIdMap.get(phb.Hoofdstuk);

    if (!hoofdstuk) continue;

    /**
     * Get/create hoofdstuk
     */
    let vigerendHoofdstuk = vigerendHoofdstukkenMap.get(hoofdstuk.Lineage);

    if (!vigerendHoofdstuk) {
      vigerendHoofdstuk = {
        data: hoofdstuk,
        paragrafen: [],
      };

      vigerendHoofdstukkenMap.set(hoofdstuk.Lineage, vigerendHoofdstuk);
    }

    /**
     * Get/create paragraaf
     */
    let vigerendParagraaf = vigerendParagrafenMap.get(paragraaf.Lineage);

    if (!vigerendParagraaf) {
      vigerendParagraaf = {
        data: paragraaf,
        phb,
        subparagrafen: [],
      };

      vigerendParagrafenMap.set(paragraaf.Lineage, vigerendParagraaf);
      vigerendHoofdstuk.paragrafen.push(vigerendParagraaf);
    }

    /**
     * Get/create subparagraaf
     */
    let vigerendSubparagraaf = vigerendSubparagrafenMap.get(
      subparagraaf.Lineage
    );

    if (!vigerendSubparagraaf) {
      vigerendSubparagraaf = {
        data: subparagraaf,
        sppb,
        voorschriften: [],
      };

      vigerendSubparagrafenMap.set(subparagraaf.Lineage, vigerendSubparagraaf);
      vigerendParagraaf.subparagrafen.push(vigerendSubparagraaf);
    }

    /**
     * Create voorschrift
     */
    const vigerendVoorschrift: VigerendVoorschrift = {
      data: voorschrift,
      vspb,
    };

    vigerendSubparagraaf.voorschriften.push(vigerendVoorschrift);
  }

  /**
   *
   * Sort the lineages by number/rang
   *
   */
  const vigerendHoofdstukken = Array.from(vigerendHoofdstukkenMap.values());
  vigerendHoofdstukken.sort((a, b) => {
    return a.data.Nummer - b.data.Nummer;
  });

  vigerendHoofdstukken.forEach((vigerendHoofdstuk) => {
    vigerendHoofdstuk.paragrafen = vigerendHoofdstuk.paragrafen.sort((a, b) => {
      return a.phb.Rang < b.phb.Rang ? -1 : 1;
    });

    vigerendHoofdstuk.paragrafen.forEach((vigerendParagraaf) => {
      vigerendParagraaf.subparagrafen = vigerendParagraaf.subparagrafen.sort(
        (a, b) => {
          return a.sppb.Rang < b.sppb.Rang ? -1 : 1;
        }
      );
    });
  });

  /**
   *
   * Group hoofdstukken by grondslagen
   *
   */
  const grondslagen = GRONDSLAGEN.map((grondslag) => {
    const grondslagHoofdstukken = vigerendHoofdstukken.filter(
      (vigerendHoofdstuk) => {
        return vigerendHoofdstuk.data.Grondslag === grondslag.id;
      }
    );

    return {
      data: grondslag,
      hoofdstukken: grondslagHoofdstukken,
    };
  }).filter((grondslag) => grondslag.hoofdstukken.length);

  return grondslagen;
}
